export type UserRole = "USER" | "ADMIN";
export type User = {
  id: string;
  publicId: string;
  email: string;
  role: UserRole;
};
export type Project = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};
export type ScreenSummary = {
  id: string;
  code: string;
  name: string;
  projectId: string | null;
  createdAt: string;
  updatedAt: string;
};
export type Screen = ScreenSummary & { data: unknown };
export type AdminOverview = {
  users: number;
  projects: number;
  screens: number;
  newUsers: number;
  disabledUsers: number;
};
export type AdminUser = User & {
  isDisabled: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  _count: { projects: number; screens: number };
};

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const request = async <T>(path: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (body && body.error) || `Request failed (${response.status})`,
    );
  }

  return body as T;
};

export const authApi = {
  signup: (email: string, password: string) =>
    request<User>("/auth/signup", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  login: (email: string, password: string) =>
    request<User>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  me: () => request<User>("/auth/me"),
};

export const projectsApi = {
  list: () => request<Project[]>("/projects"),
  create: (name: string) =>
    request<Project>("/projects", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  rename: (id: string, name: string) =>
    request<void>(`/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  remove: (id: string) =>
    request<void>(`/projects/${id}`, { method: "DELETE" }),
};

export const screensApi = {
  list: (projectId?: string | null) =>
    request<ScreenSummary[]>(
      projectId ? `/screens?projectId=${projectId}` : "/screens",
    ),
  get: (id: string) => request<Screen>(`/screens/${id}`),
  create: (params: {
    name: string;
    projectId?: string | null;
    data: unknown;
  }) =>
    request<Screen>("/screens", {
      method: "POST",
      body: JSON.stringify(params),
    }),
  update: (
    id: string,
    params: { name?: string; projectId?: string | null; data?: unknown },
  ) =>
    request<Screen>(`/screens/${id}`, {
      method: "PATCH",
      body: JSON.stringify(params),
    }),
  remove: (id: string) => request<void>(`/screens/${id}`, { method: "DELETE" }),
};

export const adminApi = {
  overview: () => request<AdminOverview>("/admin/overview"),
  users: (query = "") =>
    request<AdminUser[]>(
      `/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`,
    ),
  updateUser: (
    id: string,
    changes: { role?: UserRole; isDisabled?: boolean },
  ) =>
    request<Pick<AdminUser, "id" | "email" | "role" | "isDisabled">>(
      `/admin/users/${id}`,
      { method: "PATCH", body: JSON.stringify(changes) },
    ),
  deleteUser: (id: string) =>
    request<void>(`/admin/users/${id}`, { method: "DELETE" }),
};

export { ApiError };
